import { useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';

const MARGIN = 16;
const MIN_SIZE = 200;

const getMaxWidth = (width: number) =>
  Math.max(Math.floor(width) - MARGIN, MIN_SIZE);

const useDropdownContentSize = (
  dropdown?: DropdownRef | null,
  shouldUpdate = true,
) => {
  const dropdownContentRef = useRef<HTMLDivElement>(null);

  const updateMaxSizes = useCallback(() => {
    const el = dropdownContentRef?.current;

    if (!el) return;

    const rect = el.getBoundingClientRect();

    const availableHeight = dropdown?.isUpwards
      ? rect.bottom
      : window.innerHeight - rect.top;

    if (availableHeight)
      el.style.maxHeight = `${getMaxWidth(availableHeight)}px`;

    const availableWidth = dropdown?.isBackwards
      ? rect.right
      : window.innerWidth - rect.left;

    if (availableWidth) el.style.maxWidth = `${getMaxWidth(availableWidth)}px`;
  }, [dropdown]);

  useLayoutEffect(() => {
    const el = dropdownContentRef.current;

    if (!dropdown?.isOpen || !shouldUpdate || !el) return;

    updateMaxSizes();

    // The library flips sides before it moves the content, so measure again
    // once it has.
    let isCurrent = true;
    queueMicrotask(() => isCurrent && updateMaxSizes());

    // The library picks a side by measuring the content as it opens, so the
    // next opening has to start from the natural size, not this clamp.
    return () => {
      isCurrent = false;
      el.style.removeProperty('max-height');
      el.style.removeProperty('max-width');
    };
  }, [dropdown, shouldUpdate, updateMaxSizes]);

  useEffect(() => {
    if (!dropdown?.isOpen || !shouldUpdate) return;

    window.addEventListener('resize', updateMaxSizes);
    window.addEventListener('scroll', updateMaxSizes, true);

    return () => {
      window.removeEventListener('resize', updateMaxSizes);
      window.removeEventListener('scroll', updateMaxSizes, true);
    };
  }, [dropdown, shouldUpdate, updateMaxSizes]);

  return { dropdownContentRef };
};

export default useDropdownContentSize;
